import React, {useEffect, useMemo, useRef, useState} from 'react'
import useAdminStore from '../../stores/AdminStoreZS.js'
import useLoginStore from '../../stores/LoginStoreZS.js'
import {closeHandoverTab, openHandoverTab} from '../../utils/loginAsHandover.js'
import {H4, P, Panel, Colors, Row, Button, IconButton, Alert, Badge} from '../../components/UI/index.js'
import {DatePicker, formSpacing, Input, Select} from '../../components/UI/forms/index.js'
import Table from '../../components/UI/table/index.js'
import moment from 'moment'
import LogDetails, {TYPES, durationOf, messageOf, requestOf} from './components/logs/LogDetails.js'

// A cell value with a button that filters the logs on it, only shown while the row is hovered. Hidden, not left out: the
// value doesn't move when the button appears
// Besides orders and parcels, what an entry can be about (utils/requestContext.ts ENTITY_IDS). All are searched by ID
const OTHER_ENTITY_IDS = [
    {key: 'shiftId', title: 'Dienst ID'},
    {key: 'routeId', title: 'Route ID'},
    {key: 'invoiceId', title: 'Factuur ID'},
    {key: 'subscriptionId', title: 'Abonnement ID'}
]

function FilterCell({hovered, onFilter, children, ...rest}) {
    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 4, minWidth: 0}} {...rest}>
            {children}

            <div style={{visibility: hovered ? 'visible' : 'hidden', flexShrink: 0}}>
                <IconButton
                    icon='mdi mdi-filter'
                    tooltip='Filter op deze waarde'
                    onClick={(event) => {
                        event.stopPropagation()
                        onFilter()
                    }}
                />
            </div>
        </div>
    )
}

// The server sorts the logs newest first, the table only has one page: sorting it would only sort that page. So no
// column is sortable (a column sorts on its sort, text or key), a text or key is shown with render instead
function unsortable(column) {
    const {text, key} = column
    const render = column.render || (text || key ? (log) => <P ellipsis>{text ? text(log) : key.split('.').reduce((value, part) => value?.[part], log)}</P> : undefined)

    return {...column, sort: undefined, text: undefined, key: undefined, render}
}

const columns = (resellers, customerNames, searchOrderAndParcel, loginAsReseller, filterBy, showDetails) => {
    return [
        {id: 'dateTime', title: 'Datum & tijd', visible: true, width: 120, text: (log) => {
            return moment(log.timestamp).format('DD-MM-YYYY HH:mm')
        }},
        {id: 'resellerId', title: 'Reseller', visible: true, width: 250, key: 'resellerId', render: (log, {hovered}) => {
            if (!log?.resellerId) {
                return ''
            }

            return (
                <FilterCell hovered={hovered} onFilter={() => filterBy({resellerId: log.resellerId})}>
                    <P ellipsis>{resellers.find((reseller) => reseller._id === log.resellerId)?.name || log.resellerId}</P>
                </FilterCell>
            )
        }},
        {id: 'customerId', title: 'Klant', visible: true, width: 200, key: 'customerId', render: (log, {hovered}) => {
            if (!log?.customerId) {
                return ''
            }

            return (
                <FilterCell hovered={hovered} onFilter={() => filterBy({customerId: log.customerId})}>
                    <P ellipsis>{customerNames.get(log.customerId)?.name || log.customerId}</P>
                </FilterCell>
            )
        }},
        {id: 'userId', title: 'User ID', visible: false, width: 200, key: 'userId'},
        {id: 'orderId', title: 'Rit ID', visible: true, width: 200, key: 'orderId', render: (log, {hovered}) => {
            if (!log?.orderId) {
                return ''
            }

            return (
                <FilterCell
                    hovered={hovered}
                    onFilter={() => filterBy({id: log.orderId})}
                    onClick={() => {
                        // Opened in the click, Safari blocks tabs opened after the search
                        const win = openHandoverTab()

                        searchOrderAndParcel(log.orderId, (err, result) => {
                            const reseller = !err && resellers.find((reseller) => reseller.name === result?.order?.reseller)

                            if (reseller) {
                                loginAsReseller(reseller._id, `/planning/${log.orderId}`, undefined, win)
                            } else {
                                closeHandoverTab(win)
                            }
                        })
                    }}
                >
                    <IconButton icon='mdi mdi-eye' />
                    <P ellipsis>{log.orderId}</P>
                </FilterCell>
            )
        }},
        {id: 'parcelId', title: 'Zending ID', visible: true, width: 200, key: 'parcelId', render: (log, {hovered}) => {
            if (!log?.parcelId) {
                return ''
            }

            return (
                <FilterCell
                    hovered={hovered}
                    onFilter={() => filterBy({id: log.parcelId})}
                    onClick={() => {
                        // Opened in the click, Safari blocks tabs opened after the search
                        const win = openHandoverTab()

                        searchOrderAndParcel(log.parcelId, (err, result) => {
                            const reseller = !err && resellers.find((reseller) => reseller.name === result?.parcel?.reseller)

                            if (reseller) {
                                loginAsReseller(reseller._id, `/zendingen/${log.parcelId}`, undefined, win)
                            } else {
                                closeHandoverTab(win)
                            }
                        })
                    }}
                >
                    <IconButton icon='mdi mdi-eye' />
                    <P ellipsis>{log.parcelId}</P>
                </FilterCell>
            )
        }},
        // Hidden at first, each can be shown in the table settings
        ...OTHER_ENTITY_IDS.map(({key, title}) => ({id: key, title, visible: false, width: 200, key, render: (log, {hovered}) => {
            if (!log?.[key]) {
                return ''
            }

            return (
                <FilterCell hovered={hovered} onFilter={() => filterBy({id: log[key]})}>
                    <P ellipsis>{log[key]}</P>
                </FilterCell>
            )
        }})),
        {id: 'type', title: 'Type', visible: true, width: 110, text: (log) => {
            return TYPES.find((type) => type.value === log.type)?.title || log.type || ''
        }},
        {id: 'err.name', title: 'Naam', visible: true, width: 120, text: (log) => {
            return log.err?.name || log.action || log.subject || ''
        }},
        {id: 'message', title: 'Omschrijving', visible: true, flex: 1, render: (log) => {
            return <P ellipsis>{messageOf(log)}</P>
        }},
        {id: 'statusCode', title: 'Status', visible: true, width: 60, text: (log) => {
            return log.statusCode || log.err?.statusCode || ''
        }},
        {id: 'duration', title: 'Duur', visible: true, width: 50, text: (log) => {
            return durationOf(log) || '-'
        }},
        {id: 'requestUrl', title: 'Request url', visible: true, width: 400, render: (log) => {
            return <P ellipsis>{requestOf(log).url}</P>
        }},
        {id: 'meta.requestOptions.method', title: 'Request methode', visible: true, width: 120, text: (log) => {
            return requestOf(log).method
        }},
        {id: 'meta.origin', title: 'Origin', visible: true, flex: 1, text: (log) => {
            return log.origin || log.meta?.origin || ''
        }},
        {id: 'correlationId', title: 'Correlation ID', visible: true, width: 150, render: (log, {hovered}) => {
            if (!log.correlationId) {
                return ''
            }

            return (
                <FilterCell hovered={hovered} onFilter={() => filterBy({correlationId: log.correlationId})}>
                    <P ellipsis>{log.correlationId}</P>
                </FilterCell>
            )
        }},
        // At the end of the row while it is hovered, no title: everything of the entry, also the stack trace
        {id: 'details', visible: true, width: 40, showOnHover: true, render: (log) => {
            return <IconButton icon='mdi mdi-eye' tooltip='Details' onClick={() => showDetails(log)} />
        }}
    ].map(unsortable)
}

// All filters of a search are in the url: a link to the page shows the same logs, e.g. in a ticket. The logs buttons
// of a customer, reseller, order or parcel link here with their id
const URL_FILTERS = ['types', 'resellerId', 'customerId', 'id', 'correlationId', 'startDate', 'endDate']

function filtersFromUrl() {
    const searchParams = new URLSearchParams(location.search)
    const filters = {}

    URL_FILTERS.forEach((key) => {
        const value = searchParams.get(key)

        if (value) {
            filters[key] = key === 'types' ? value.split(',').filter((type) => TYPES.some((option) => option.value === type)) : value
        }
    })

    return filters
}

function urlOf(search) {
    const searchParams = new URLSearchParams()

    URL_FILTERS.forEach((key) => {
        const value = key === 'types' ? search.types?.join(',') : search[key]

        if (value) {
            searchParams.set(key, value)
        }
    })

    return `/admin/logs${searchParams.toString() ? `?${searchParams}` : ''}`
}

// Logs are kept 30 days (db.logs TTL): older dates can't be picked
const LOGS_DAYS = 30

// Without dates: all logs
const NO_DATES = {startDate: '', endDate: ''}

// Fields that are emptied by a search, their filter is then a pill. Only indexed fields of db.logs can be searched
const INPUT_FILTERS = ['resellerId', 'customerId', 'id', 'correlationId']

function withoutInputs(search) {
    return {...search, ...Object.fromEntries(INPUT_FILTERS.map((key) => [key, ''])), types: [], ...NO_DATES}
}

function emptySearch() {
    return {types: [], resellerId: '', customerId: '', id: '', correlationId: '', ...NO_DATES}
}

// The filters of a search as pills: label and the search without that filter
function activeFilters(search, resellers, customerNames) {
    const date = (value) => moment(value).format('DD-MM-YYYY')
    const dates = search.startDate === search.endDate ? date(search.startDate) : `${date(search.startDate)} t/m ${date(search.endDate)}`
    const reseller = (id) => resellers.find((reseller) => reseller._id === id)?.name

    const filters = [
        ...(search.types || []).map((type) => ({
            key: `type-${type}`,
            label: `Type: ${TYPES.find((option) => option.value === type)?.title || type}`,
            without: {types: search.types.filter((value) => value !== type)}
        })),
        {key: 'resellerId', label: `Reseller: ${reseller(search.resellerId) || search.resellerId}`},
        {key: 'customerId', label: `Klant: ${customerNames.get(search.customerId)?.name || search.customerId}`},
        {key: 'id', label: `ID: ${search.id}`},
        {key: 'correlationId', label: `Correlation ID: ${search.correlationId}`},
        {key: 'dates', label: `Datum: ${dates}`, without: NO_DATES, active: !!search.startDate}
    ]

    return filters.filter((filter) => filter.active ?? (filter.without || search[filter.key]?.trim?.()))
}

export default function Logs({history}) {
    const logs = useAdminStore((state) => state.logs)
    const getLogs = useAdminStore((state) => state.getLogs)
    const totalLogs = useAdminStore((state) => state.totalLogs)
    const logsLoading = useAdminStore((state) => state.logsLoading)
    const logsError = useAdminStore((state) => state.logsError)
    const resellers = useAdminStore((state) => state.resellers)
    const getResellers = useAdminStore((state) => state.getResellers)
    const customerList = useAdminStore((state) => state.customerNames)
    const getCustomerNames = useAdminStore((state) => state.getCustomerNames)
    // By id for the names in the table, the pills and the details
    const customerNames = useMemo(() => new Map(customerList.map((customer) => [customer._id, customer])), [customerList])
    const searchOrderAndParcel = useAdminStore((state) => state.searchOrderAndParcel)
    const loginAsReseller = useLoginStore((state) => state.loginAsReseller)
    const tableRef = useRef()
    const [detailsLog, setDetailsLog] = useState(null)
    // The filters of the last search, shown as pills: other pages are of that search, not of filters that are changed
    // but not searched yet
    const searchedRef = useRef(null)
    searchedRef.current ??= {...emptySearch(), ...filtersFromUrl()}
    const [searched, setSearched] = useState(searchedRef.current)

    // What is filled in: the fields are emptied by a search, the pills show their filters
    const [search, setSearch] = useState(() => withoutInputs(searchedRef.current))

    // Of the chosen reseller, or else of the searched one. The reseller next to the name: customers of different
    // resellers can have the same name
    const customerReseller = resellers.find((reseller) => reseller._id === (search.resellerId || searched.resellerId))?.name
    // The same array until the list or the reseller changes: the Select then doesn't compare thousands of options
    const customerOptions = useMemo(() => customerList
        .filter((customer) => !customerReseller || customer.reseller === customerReseller)
        .map((customer) => ({value: customer._id, title: customer.name, subvalue: customer.reseller})), [customerList, customerReseller])

    // A chosen customer of another reseller is emptied when a reseller is chosen
    const onChange = (key, event) => {
        const newSearch = {...search, [key]: key === 'types' ? event.target.values : event.target.value}
        const reseller = key === 'resellerId' && resellers.find((reseller) => reseller._id === newSearch.resellerId)

        if (reseller && search.customerId && customerNames.get(search.customerId)?.reseller !== reseller.name) {
            newSearch.customerId = ''
        }

        setSearch(newSearch)
    }

    const onPagination = (pageSize, page) => {
        getLogs(pageSize, page, searchedRef.current)
    }

    // A new search starts at page 1, the table only calls onPagination for the pages the user picks
    const fetchLogs = (newSearch) => {
        const pageSize = localStorage.getItem('_pageSize') || 40
        const url = urlOf(newSearch)

        if (url !== `${location.pathname}${location.search}`) {
            history.replace(url)
        }

        searchedRef.current = newSearch
        setSearched(newSearch)
        setSearch(withoutInputs(newSearch))
        getLogs(+pageSize, 1, newSearch)
        tableRef.current?.selectPage(1)
    }

    // A filled in field is added to the filters of the last search (chosen types to the searched ones), an empty one
    // leaves its filter as it is
    const onSearch = () => {
        const filled = INPUT_FILTERS.filter((key) => search[key]?.trim()).map((key) => [key, search[key].trim()])
        const types = [...new Set([...searchedRef.current.types, ...search.types])]
        const dates = search.startDate ? {startDate: search.startDate, endDate: search.endDate} : {}

        fetchLogs({...searchedRef.current, types, ...dates, ...Object.fromEntries(filled)})
    }

    // From a cell (reseller, customer, order, parcel or correlation ID): the last search with this filter, all types
    const filterBy = (filters) => {
        fetchLogs({...searchedRef.current, types: [], ...filters})
    }

    const onKeyDown = (event) => {
        if (event.key === 'Enter') {
            onSearch()
        }
    }

    useEffect(() => {
        fetchLogs(searchedRef.current)
        getResellers()
        getCustomerNames()
    }, [])

    // filterBy only reads refs, the columns don't have to change with it
    const tableColumns = useMemo(() => columns(resellers, customerNames, searchOrderAndParcel, loginAsReseller, filterBy, setDetailsLog), [resellers, customerNames, searchOrderAndParcel, loginAsReseller])

    const filterPills = activeFilters(searched, resellers, customerNames)

    return (
        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Panel style={{background: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>
                <H4 style={{marginBottom: 12}}>Logs</H4>

                <Row style={{columnGap: formSpacing.columnGap, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center'}}>
                    <DatePicker
                        style={{width: 200, flex: 'none'}}
                        label='Datum'
                        value={search.startDate}
                        range={{startDate: search.startDate, endDate: search.endDate}}
                        minDate={moment().subtract(LOGS_DAYS, 'days').format('YYYY-MM-DD')}
                        onChange={(range) => range.startDate && setSearch({...search, startDate: range.startDate, endDate: range.endDate})}
                    />

                    <Select
                        style={{width: 250}}
                        label='Type'
                        info='Wordt toegevoegd aan de filters. Zonder type filter: alle types'
                        options={TYPES}
                        values={search.types}
                        onChange={(event) => onChange('types', event)}
                    />

                    <Select
                        style={{width: 250}}
                        label='Reseller'
                        allowEmpty
                        options={resellers.map((reseller) => ({value: reseller._id, title: reseller.name}))}
                        value={search.resellerId}
                        onChange={(event) => onChange('resellerId', event)}
                    />

                    <Select
                        style={{width: 250}}
                        label='Klant'
                        allowEmpty
                        options={customerOptions}
                        value={search.customerId}
                        onChange={(event) => onChange('customerId', event)}
                    />

                    <Input
                        value={search.id}
                        onChange={(event) => onChange('id', event)}
                        onKeyDown={onKeyDown}
                        style={{width: 250}}
                        label='ID'
                        info='Van een rit, zending, dienst, route, factuur of abonnement'
                    />

                    <Input
                        value={search.correlationId}
                        onChange={(event) => onChange('correlationId', event)}
                        onKeyDown={onKeyDown}
                        style={{width: 300}}
                        label='Correlation ID'
                        info='Alles van één API request (X-Request-Id), socket event, cronjob, webhook of import. De code van een foutmelding in de app (8 tekens) werkt ook'
                    />

                    <Button
                        icon='mdi mdi-magnify'
                        label='Zoeken'
                        onClick={onSearch}
                    />

                    <IconButton
                        icon='mdi mdi-link-variant'
                        tooltip='Link naar deze logs kopiëren'
                        onClick={() => navigator.clipboard?.writeText(location.href).catch(() => {})}
                    />
                </Row>

                {filterPills.length > 0 &&
                    <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8}}>
                        {filterPills.map((filter) => (
                            <Badge key={filter.key} variant='white' style={{paddingRight: 2, gap: 2}}>
                                {filter.label}

                                <IconButton
                                    icon='mdi mdi-close'
                                    aria-label='Filter verwijderen'
                                    style={{width: 20, height: 20, fontSize: 14}}
                                    onClick={() => fetchLogs({...searchedRef.current, ...filter.without || {[filter.key]: ''}})}
                                />
                            </Badge>
                        ))}

                        {filterPills.length > 1 &&
                            <Button
                                variant='text'
                                label='Alles wissen'
                                onClick={() => fetchLogs(emptySearch())}
                            />
                        }
                    </div>
                }
            </Panel>

            {logsError &&
                <Alert style={{marginTop: 12, marginLeft: 24, marginRight: 24}} variant='error'>{logsError}</Alert>
            }

            <div style={{flex: 1, marginTop: 24, marginLeft: 24, marginRight: 24}}>
                <Table
                    tableName='logs'
                    legacyTableName='undefined'
                    columns={tableColumns}
                    rows={logs}
                    selectedRows={[]}
                    loading={logsLoading}
                    emptyText='Geen logs gevonden'
                    onPagination={onPagination}
                    totalRowsCount={totalLogs}
                    serverSidePagination
                    ref={tableRef}
                />
            </div>

            <LogDetails
                log={detailsLog}
                resellers={resellers}
                customerNames={customerNames}
                onClose={() => setDetailsLog(null)}
                onFilter={(filters) => {
                    setDetailsLog(null)
                    filterBy(filters)
                }}
            />
        </div>
    )
}
